import React, { useEffect, useState } from "react";
import { collection, deleteDoc, doc, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../../firebase";
import { showAlert } from "../../alertService";
import { logBitacora, BITACORA_ACTIONS } from "../../utils/bitacora";
import { recomputeProveedorRatingAggregates } from "../../utils/proveedores";
import { Alert } from "../alert";
import { Button } from "../button";
import ProveedorReviewDialog from "../ProveedorReviewDialog";
import { ACCENT, BTN_PRIMARIO } from "./shared";
import { PlusIcon, StarIcon, TrashIcon, PencilSquareIcon } from "@heroicons/react/20/solid";

// ---------- Reviews tab ----------
const ReviewsTab = ({ proveedor, venueId, userInfo, user, soloLectura = false }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogState, setDialogState] = useState({ open: false, existing: null });
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  useEffect(() => {
    if (!venueId || !proveedor?.id) return;
    const q = query(
      collection(db, "venues", venueId, "proveedores", proveedor.id, "reviews"),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setReviews(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("Reviews snapshot error:", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [venueId, proveedor?.id]);

  const handleDelete = async () => {
    if (!deleteCandidate || !venueId || !proveedor?.id) return;
    try {
      await deleteDoc(
        doc(
          db,
          "venues",
          venueId,
          "proveedores",
          proveedor.id,
          "reviews",
          deleteCandidate.id
        )
      );
      logBitacora(
        venueId,
        {
          action: BITACORA_ACTIONS.PROVEEDOR_REVIEW_DELETE,
          entity: "proveedor_review",
          entityId: deleteCandidate.id,
          entityName: proveedor?.nombre || "",
          details: {
            proveedorId: proveedor.id,
            proveedorNombre: proveedor?.nombre || "",
          },
        },
        userInfo,
        user
      );
      recomputeProveedorRatingAggregates(venueId, proveedor.id).catch((e) =>
        console.warn("recomputeProveedorRatingAggregates failed:", e)
      );
      setDeleteCandidate(null);
      showAlert("Reseña eliminada", "success");
    } catch (e) {
      console.error(e);
      showAlert("Error al eliminar: " + e.message, "error");
    }
  };

  const formatDate = (ts) => {
    if (!ts?.toDate) return "";
    const d = ts.toDate();
    return d.toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-zinc-500">
          {reviews.length === 0
            ? "Sin reseñas todavía"
            : `${reviews.length} reseña${reviews.length === 1 ? "" : "s"}`}
        </div>
        {!soloLectura && (
        <button
          onClick={() => setDialogState({ open: true, existing: null })}
          className={BTN_PRIMARIO}
          style={{ backgroundColor: ACCENT }}
        >
          <PlusIcon className="h-4 w-4" />
          Agregar reseña
        </button>
        )}
      </div>

      {loading ? (
        <div className="py-12 text-center text-zinc-400">Cargando...</div>
      ) : reviews.length === 0 ? (
        <div className="py-12 text-center text-zinc-400 italic">
          Aún no hay reseñas. Agrega la primera para llevar registro.
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div
              key={r.id}
              className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  {typeof r.rating === "number" && (
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="inline-flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <StarIcon
                            key={n}
                            className={`h-4 w-4 ${
                              n <= r.rating ? "text-amber-400" : "text-zinc-200"
                            }`}
                          />
                        ))}
                      </span>
                    </div>
                  )}
                  <div className="text-sm text-zinc-800 whitespace-pre-wrap">
                    {r.comentario}
                  </div>
                  <div className="mt-2 text-xs text-zinc-400">
                    {r.authorName || "Anónimo"} · {formatDate(r.createdAt)}
                    {r.updatedAt &&
                      r.createdAt &&
                      r.updatedAt.toMillis &&
                      r.createdAt.toMillis &&
                      r.updatedAt.toMillis() - r.createdAt.toMillis() > 1000 &&
                      ` · editada ${formatDate(r.updatedAt)}`}
                  </div>
                </div>
                {!soloLectura && (
                <div className="flex gap-1">
                  <button
                    onClick={() =>
                      setDialogState({ open: true, existing: r })
                    }
                    className="text-zinc-400 hover:text-zinc-600 p-1"
                    title="Editar"
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setDeleteCandidate(r)}
                    className="text-zinc-400 hover:text-red-500 p-1"
                    title="Eliminar"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ProveedorReviewDialog
        open={dialogState.open}
        onClose={() => setDialogState({ open: false, existing: null })}
        proveedorId={proveedor.id}
        proveedorNombre={proveedor.nombre}
        existing={dialogState.existing}
      />

      <Alert open={!!deleteCandidate} onClose={() => setDeleteCandidate(null)}>
        <div className="font-semibold mb-2">¿Eliminar esta reseña?</div>
        <div className="mb-4 text-sm text-zinc-600">
          Esta acción no se puede deshacer.
        </div>
        <div className="flex gap-2 justify-end">
          <Button outline onClick={() => setDeleteCandidate(null)}>
            Cancelar
          </Button>
          <Button color="red" onClick={handleDelete}>
            Eliminar
          </Button>
        </div>
      </Alert>
    </div>
  );
};


export default ReviewsTab;
